<script lang="ts">
	//UI
	import { PhotoIcon } from "@hitit/new_ui";
	import DragAndDrop from "@hitit/hub/src/components/DragAndDrop.svelte";
	import AreYouSure from "@hitit/hub/src/v7/components/common/AreYouSure.svelte";
	import ComponentLoading from "@hitit/hub/src/v7/components/common/ComponentLoading.svelte";
	import profileImageFallback from "../../../assets/profile.png?raw-base64";
	//Utils
	import { popup } from "@hitit/hub/src/v7/store";
	import { t, api } from "@hitit/hub/src/stores";
	import { OBJECT_PURPOSE_PROFILE_IMAGE } from "@hitit/api";
	import axios from "axios";
	//Types
	//Props
	export let userId = "";
	//Constants
	//Variables
	let image: string = "";
	let mouseenter = false;
	let loading = false;

	//Functions
	async function updateImage(e: { detail: File }) {
		try {
			loading = true;
			const file = e.detail;
			const extMatch = file.name.match(/\.([^\.]+)$/);
			if (!extMatch) throw new Error("format not match");
			const r = await $api.getFileUploadUrl({
				fileName: file.name.replace(/\.([^\.]+)$/, ".png"),
				fileType: "PNG",
				objectPurpose: OBJECT_PURPOSE_PROFILE_IMAGE,
				userId: userId,
			});

			await axios.put(r.data.url, file);
			changeImage(file);
		} catch (e) {
			console.log(e);
			alert($t("sso.error.unknownError"));
		} finally {
			loading = false;
		}
	}
	function deleteHandler() {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "hub.profile.are_you_sure_you_want_delete_profile_img",
				accept: () => {
					setDefaultImage();
					$popup = undefined;
				},
				cancel: () => ($popup = undefined),
			},
		};
	}

	function setDefaultImage() {
		let arr = profileImageFallback.split(","),
			mime = arr[0].match(/:(.*?);/)[1],
			bstr = atob(arr[1]),
			n = bstr.length,
			u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		const file = new File([u8arr], "defaultImage.png", { type: mime });
		updateImage({ detail: file });
	}
	async function getProfileImage(userId: string) {
		try {
			const url = `${"HITIT_IMAGE_URL"}profile/${userId}.png?v=${Date.now()}`;
			const r = await axios.get(url, {
				withCredentials: true,
				responseType: "blob",
			});
			changeImage(r.data);
		} catch (e) {
			showDefaultImage();
		}
	}
	function showDefaultImage() {
		let arr = profileImageFallback.split(","),
			mime = arr[0].match(/:(.*?);/)[1],
			bstr = atob(arr[1]),
			n = bstr.length,
			u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		const file = new File([u8arr], "defaultImage.png", { type: mime });
		changeImage(file);
	}

	function changeImage(data: File | Blob) {
		if (image) URL.revokeObjectURL(image);
		image = URL.createObjectURL(data);
	}
	//Do
	$: ((userId: string) => {
		getProfileImage(userId);
	})(userId);
	$: image ? (mouseenter = false) : (mouseenter = true);
</script>

<div class="profile-image-box">
	<img
		id="profile-image"
		src={image}
		alt="profile"
		on:error={() => console.log("error")}
	/>
	<div
		id="image-cover"
		class:loading={loading}
		class:enter={mouseenter}
		on:dragover={() => {
			mouseenter = true;
		}}
		on:dragleave={() => {
			mouseenter = false;
		}}
		on:mouseleave={() => {
			mouseenter = false;
		}}
		on:mouseenter={() => {
			mouseenter = true;
		}}
		on:touchstart={() => {
			mouseenter = true;
		}}
		on:touchend={() => {
			mouseenter = false;
		}}
	>
		{#if loading}
			<ComponentLoading />
		{:else}
			<DragAndDrop on:file-update={updateImage} let:enter>
				<div class="drop-inner">
					<div class="photoicon">
						<PhotoIcon active={mouseenter || enter} />
					</div>
					<div class="description">
						<span>{$t("hub.store.ko_registration.Click")}</span>
						<span class="or">{$t("hub.store.ko_registration.or")}</span>
						<span>{$t("hub.store.ko_registration.Drop")}</span>
					</div>
				</div>
			</DragAndDrop>
		{/if}
	</div>
	<img
		class="delete-icon"
		src="/img/icons/deleteIcon.png"
		alt={"delete"}
		on:click={deleteHandler}
	/>
</div>

<style lang="scss">
	@import "../../../variables.scss";

	.profile-image-box {
		width: 250px;
		height: 250px;
		position: relative;

		&:hover {
			cursor: pointer;
		}

		#profile-image {
			box-sizing: border-box;
			width: 250px;
			height: 250px;
			border-radius: 999px;
			border: solid 2px #6b93f3;
			object-fit: cover;
		}

		#image-cover {
			position: absolute;
			width: 246px;
			height: 246px;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			background-color: rgba($color: #46474b, $alpha: 0.6);
			border-radius: 999px;

			.drop-inner {
				display: flex;
				flex-direction: column;
				justify-content: center;
				align-items: center;
				width: 100%;
				height: 100%;
				.photoicon {
					width: 50px;
					height: 50.2px;
				}

				.description {
					display: none;
				}
			}
		}
		.delete-icon {
			position: absolute;
			right: -30px;
			bottom: 0;
			width: 30px;
			height: 30.2px;
		}
	}

	@include DesktopSize {
		.profile-image-box {
			width: 350px;
			height: 350px;

			#profile-image {
				box-sizing: border-box;
				width: 350px;
				height: 350px;
				border-radius: 999px;
				box-sizing: border-box;
				border: solid 4px #6b93f3;
			}

			#image-cover {
				width: 346px;
				height: 346px;
				opacity: 0;
				transition: opacity 0.5s;
				&.loading {
					opacity: 1;
				}
				&.enter {
					opacity: 1;
				}

				.drop-inner {
					gap: 23px;
					.description {
						display: flex;
						align-items: baseline;
						gap: 20px;

						font-weight: 500;
						font-size: 1.25rem;
						color: white;
						.or {
							color: #bcbfc3;
							font-size: 1rem;
							font-weight: 300;
						}
					}
				}
			}
			.delete-icon {
				right: 0px;
				width: 36px;
				height: 28.6px;
				object-fit: contain;
			}
		}
	}
</style>
